import axios from "axios";
import { useParams } from "react-router-dom";
import Logo from "../components/logo/Logo";
import { Download, MapPin } from "lucide-react";
import { useState, useEffect } from "react";

function EstimedClosingLayout() {
  const { tokenId } = useParams();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState("");

  const handleClick = async (e) => {
    e.preventDefault();
    setError(null);
    setDownloading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/download-pdf/${tokenId}`;
      const response = await axios({
        url,
        method: "GET",
        responseType: "blob",
      });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "Estimated_Closing_Costs.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error downloading the PDF:", err);
      setError("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchTokenDetails = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get-token-details/token/${tokenId}`);
        const data = response?.data?.data ?? response?.data;
        const addr = data?.address ?? "";
        setAddress(addr);
      } catch (err) {
        console.error("Error fetching token details:", err);
      }
    };
    if (tokenId) fetchTokenDetails();
  }, [tokenId]);

  return (
    <>
      <style>
        {`
          .estimated-closing-root {
            box-sizing: border-box;
            min-height: 100vh;
            min-height: 100dvh;
            padding: max(12px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
            background: linear-gradient(160deg, #f0f4ff 0%, #e8eeff 50%, #f6f8fc 100%);
            font-family: "Poppins", -apple-system, BlinkMacSystemFont, sans-serif;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .estimated-closing-card {
            width: 100%;
            max-width: 360px;
            background: #fff;
            border-radius: 20px;
            box-shadow: 0 4px 24px rgba(9, 29, 242, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
            border: 1px solid rgba(9, 29, 242, 0.06);
            padding: 24px 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
          }
          .estimated-closing-logo {
            width: 140px;
          }
          .estimated-closing-logo img {
            width: 100%;
            height: auto;
          }
          .estimated-closing-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: linear-gradient(135deg, rgba(9, 29, 242, 0.1) 0%, rgba(99, 102, 241, 0.08) 100%);
            color: #091DF2;
            font-size: 12px;
            font-weight: 600;
            padding: 6px 12px;
            border-radius: 20px;
            letter-spacing: 0.02em;
          }
          .estimated-closing-title {
            text-align: center;
            font-size: 18px;
            font-weight: 600;
            color: #1e293b;
            line-height: 1.4;
            margin: 0;
          }
          .estimated-closing-location {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 15px;
            font-weight: 500;
            color: #475569;
          }
          .estimated-closing-location svg {
            flex-shrink: 0;
            color: #091DF2;
          }
          .estimated-closing-btn {
            width: 100%;
            max-width: 300px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            background: linear-gradient(135deg, #091DF2 0%, #4f46e5 100%);
            color: #fff;
            font-size: 16px;
            font-weight: 600;
            font-family: inherit;
            padding: 16px 24px;
            border: none;
            border-radius: 14px;
            cursor: pointer;
            box-shadow: 0 4px 14px rgba(9, 29, 242, 0.4);
            transition: transform 0.15s ease, box-shadow 0.15s ease;
            -webkit-tap-highlight-color: transparent;
          }
          .estimated-closing-btn:active {
            transform: scale(0.98);
          }
          .estimated-closing-btn:disabled {
            opacity: 0.85;
            cursor: not-allowed;
            transform: none;
          }
          .estimated-closing-btn svg {
            flex-shrink: 0;
          }
          .estimated-closing-error {
            font-size: 13px;
            color: #dc2626;
            text-align: center;
          }
          .estimated-closing-footer {
            margin-top: auto;
            padding-top: 20px;
            font-size: 11px;
            color: #94a3b8;
            text-align: center;
          }
        `}
      </style>
      <div className="estimated-closing-root">
        <div className="estimated-closing-card">
          <div className="estimated-closing-logo">
            <Logo type="primary" size={140} />
          </div>
          <span className="estimated-closing-badge">
            
          </span>
          <h1 className="estimated-closing-title">
            Congratulations on your
            <br />
            upcoming transaction
          </h1>
          <div className="estimated-closing-location">
            <MapPin size={20} />
            {address || ""}
          </div>
          <button
            type="button"
            onClick={handleClick}
            disabled={downloading}
            className="estimated-closing-btn"
          >
            <Download size={22} />
            {downloading ? "Downloading…" : "Your Estimated Closing Costs"}
          </button>
          {error && <p className="estimated-closing-error">{error}</p>}
        </div>
        <p className="estimated-closing-footer">
          © {new Date().getFullYear()} Turning Point Software. All rights reserved.
        </p>
      </div>
    </>
  );
}

export default EstimedClosingLayout;
